import { Box } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import { useDispatch } from "react-redux";
import { workflowSteps, buildWorkflowSteps } from "../../const/WorkflowSteps";
import { VerticalStepper } from "./VerticalStepper";
import { StepTitle } from "./StepTitle";
import { Categories } from "./categories/Categories";
import { TypeOfService } from "./type-of-service/TypeOfService";
import { Areas } from "./areas/Areas";
import { TypeOfPlaces } from "./type-of-places/TypeOfPlaces";
import { useAppSelector } from "../../../../../../../redux/hooks/redux";
import {
  selectSectorStep,
  selectSectorWorkflow,
} from "../../../../../../../redux/features/quotes/quote-create-advanced/selectors/sectorWorkflowSelectors";
import { quoteCreateActions } from "../../../../../../../redux/features/quotes/quote-create-advanced";
import { Objects } from "./objects/Objects";
import { SpecialRequests } from "./special-request/SpecialRequests";
import { fetchSpecialRequests } from "./special-request/actions/fetchSpecialRequests.actions";

export const SectorWorkflowOrchestrator = () => {
  const dispatch = useDispatch();
  const step = useAppSelector(selectSectorStep);
  const workflowData = useAppSelector(selectSectorWorkflow);
  const specialRequestsStepEnabled = workflowData?.specialRequestsStepEnabled;
  const { category, places, service } = workflowData || {};

  const categoryParam = category?.uid ?? category?.name ?? "";

  const specialRequestsCountQuery = useQuery({
    queryKey: ["sector-workflow-special-requests-count", categoryParam],
    queryFn: () => fetchSpecialRequests(1, 1, categoryParam ? { category: categoryParam } : undefined),
    enabled: !!(category?.uid || category?.name),
    staleTime: 1000 * 60 * 5,
    retry: 1,
  });

  useEffect(() => {
    const total = specialRequestsCountQuery.data?.totalItems;
    if (typeof total !== "number") return;
    dispatch(quoteCreateActions.setSpecialRequestsStepEnabled(total > 0));
  }, [dispatch, specialRequestsCountQuery.data?.totalItems]);

 const resolvedWorkflowSteps = useMemo(
  () => buildWorkflowSteps(specialRequestsStepEnabled === true),
  [specialRequestsStepEnabled],
);

useEffect(() => {
  const currentStepExists = resolvedWorkflowSteps.some(
    (workflowStep) => workflowStep.id === step,
  );

  if (currentStepExists || resolvedWorkflowSteps.length === 0) {
    return;
  }

  const lastAvailableStep =
    resolvedWorkflowSteps[resolvedWorkflowSteps.length - 1];

  dispatch(
    quoteCreateActions.setSectorStep(lastAvailableStep.id),
  );
}, [dispatch, resolvedWorkflowSteps, step]);

  const stepComponents = {
    categories: <Categories selected={category} />,
    "type-of-places": <TypeOfPlaces selected={places} categoryType={category?.name} />,
    "type-of-service": <TypeOfService selected={service} categoryType={category?.name} />,
    areas: <Areas />,
    objects: <Objects />,
    "special-requests": <SpecialRequests />,
  };

  return (
    <Box sx={{ display: "flex", width: "100%", height: "100%" }}>
      <Box
        sx={{
          width: "19%",
          height: "100%",
          position: "block",
          top: 0,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          background: "var(--fx-surface-base, #FFFFFF)",
          zIndex: 10,
        }}
      >
        <VerticalStepper steps={resolvedWorkflowSteps} currentStep={step} />
      </Box>

      <Box
        sx={{
          width: "78%",
          height: "100%",
          overflowY: "auto",
        }}
      >
        <StepTitle
          title={resolvedWorkflowSteps.find((s) => s.id === step)?.label || workflowSteps.find((s) => s.id === step)?.label || ""}
          subtitle={
            resolvedWorkflowSteps.find((s) => s.id === step)?.description ||
            workflowSteps.find((s) => s.id === step)?.description ||
            "Complete this step to continue your setup."
          }
        />

        <Box>{stepComponents[step] || null}</Box>
      </Box>
    </Box>
  );
};
